import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

const USVisa = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const fileInputRef = React.useRef(null);

  const [expiryDate, setExpiryDate] = useState("");

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // 🔍 Fetch US Visa info
  const fetchVisa = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/usvisa/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // ✅ expiry date
      setExpiryDate(
        res.data?.uv_expiry_date
          ? new Date(res.data.uv_expiry_date).toISOString().split("T")[0]
          : ""
      );

      // ✅ multiple files
      setUploadedFiles(res.data?.files || []);

      // 🔄 reset delete tracker on reload
      setFilesToDelete([]);
    } catch (err) {
      console.error("❌ Fetch US Visa failed:", err);

      // reset on no record / error
      setExpiryDate("");
      setUploadedFiles([]);
      setFilesToDelete([]);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchVisa();
  }, [fetchVisa]);

  // 🔍 Check Signature Status
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const TASK_ID = 43; // ✅ US Visa Task ID
        const nameKey = "us_visa";
        const idKey = String(TASK_ID);

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("uv_expiry_date", expiryDate);
    formData.append("filesToDelete", JSON.stringify(filesToDelete));

    // ✅ multiple files
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(
        `${API_BASE}/api/usvisa/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert(response.data.message || "Saved successfully!");

      // reset local selections
      setFiles([]);
      setFilesToDelete([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }

      fetchVisa();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Save failed. Please try again.");
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          US Visa Document
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">US Visa</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label>Expiry Date</label>
              <input
                type="date"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={!isAdmin && isSigned}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Select File (PDF / JPG / PNG) *</label>
              <input
                type="file"
                className="form-control"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                ref={fileInputRef}
                onChange={(e) => setFiles([...e.target.files])}
                disabled={!isAdmin && isSigned}
              />
            </div>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded Files</div>

              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <div>
                    <a
                      href={`${API_BASE}/uploads/usvisas/${file.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.original_name}
                    </a>
                    <div className="text-muted">
                      Uploaded on: {new Date(file.uploaded_at).toLocaleString()}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={!isAdmin && isSigned}
                    onClick={() => {
                      setFilesToDelete((prev) => [...prev, file.id]);
                      setUploadedFiles((prev) =>
                        prev.filter((f) => f.id !== file.id)
                      );
                    }}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && isSigned}
            >
              Save
            </button>
          </div>
        </form>

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="us_visa"
              taskId={43}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="us_visa"
              taskId={43}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default USVisa;
